import { MenuButton } from '../../vendor/elix.js';
import { internal } from '../../vendor/elix.js';
import { template } from '../../vendor/elix.js';
import TpPopup from './tp-popup.js';
import { HoverMixin } from '../../vendor/elix.js';

class TpMenu extends HoverMixin(MenuButton) {

  static get observedAttributes() {
    return [
      ...super.observedAttributes,
      'hoveractivation',
      'active',
      'buttontext'
    ];
  }

  get active() {
    return this[internal.state].active;
  }

  set active(v) {
    this[internal.setState]({ active: v });
  }

  get buttontext() {
    return this[internal.state].buttontext;
  }

  set buttontext(v) {
    this[internal.setState]({ buttontext: v });
  }


  get activeItem() {
    return this[internal.state].activeItem;
  }

  set activeItem(v) {
    this[internal.setState]({ activeItem: v });
  }

  get hoveractivation() {
    return this[internal.state].hoveractivation;
  }

  set hoveractivation(value) {
    this[internal.setState]({ hoveractivation: value });
  }

  getItems() {
    return Array.from(this.children)
      .filter(x => !(x.hasAttribute('slot') && x.getAttribute('slot') === 'source'))
  }

  onPopupClose({detail}) {
    const { closeResult } = detail;
    if (closeResult) {
      this.activeItem = closeResult;
    }
  }

  initSlotAttributeObservers() {
    const createAttributeListener = element => new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'active-item') {
          this.activeItem = element.hasAttribute('active-item') ? element : null;
        }
      });
    });
    const arr = this.getItems().map((element) => ({element, observer: createAttributeListener(element)}));

    this.connectSlotAttributeObservers = () => arr.forEach(m => m.observer.observe(m.element, {attributes: true}));
    this.disconnectSlotAttributeObservers = () => arr.forEach(m => m.observer.disconnect());
  }

  connectedCallback() {
    super.connectedCallback();
    this.activeItem = this.getItems().find(x => x.hasAttribute('active-item'));
    this.addEventListener('closed', this.onPopupClose);
    this.shadowRoot.querySelector('#source').addEventListener('mousedown', (e) => {
      if (this[internal.state].hover && this.hoveractivation) {
        e.preventDefault();
      }
    });
    this.initSlotAttributeObservers();
    this.connectSlotAttributeObservers();
  }

  get [internal.defaultState]() {
    return Object.assign(super[internal.defaultState], {
      'hoveractivation': true,
      popupPartType: TpPopup,
      buttontext: null
    });
  }

  attributeChangedCallback(attributeName, oldValue, newValue) {
    super.attributeChangedCallback(attributeName, oldValue, newValue);

    const isTrueString = str => String(str) === 'true';
    const customAttributes = {
      hoveractivation: v => this.hoveractivation = isTrueString(v),
      active: v => this.active = isTrueString(v),
      buttontext: v => this.buttontext = v
    };

    if (Object.keys(customAttributes).includes(attributeName)) {
      customAttributes[attributeName](newValue)
    }
  }

  [internal.render](changed) {
    const setActiveStateOnSource = (isActive) => {
      if (!this.shadowRoot) { return; }
      const source = this.shadowRoot.querySelector('#source');
      isActive
        ? source.classList.add('active')
        : source.classList.remove('active');
    };

    if (changed.opened) {
      const isActive = this[internal.state].opened ? true : (!!this.activeItem || this.active);
      setActiveStateOnSource(isActive);
    }

    super[internal.render](changed);

    if (changed.active) {
      setActiveStateOnSource(this.active);
    }

    if (changed.activeItem) {
      if (this.activeItem) {
        this.disconnectSlotAttributeObservers();
        this.getItems().forEach(x => x.removeAttribute('active-item'));
        this.activeItem.setAttribute('active-item', '');
        this.connectSlotAttributeObservers();
      }
      setActiveStateOnSource(!!this.activeItem);
    }

    if (changed.hover && this.hoveractivation) {
      if (this[internal.state].hover) {
        setTimeout(() => this.open(), 0);
      } else if (!this[internal.state].hover) {
        setTimeout(() => this.close(), 0);
      }
    }

    if (changed.buttontext) {
      this.shadowRoot.querySelector('#source').innerHTML = this.buttontext;
    }
  }

  get [internal.template]() {
    let baseTemplate = template.concat(
      super[internal.template],
      template.html`
        <style>
            #source {
                --webkit-font-smoothing: antialiased;
                background: none !important;
                border: 0;
                border-bottom: 2px solid rgba(255, 255, 255, 0);
                color: var(--tp-menu-button-color) !important;
                cursor: pointer;
                font-family: var(--tp-font-subtitle-2-font-family) !important;
                font-size: var(--tp-font-subtitle-2-font-size) !important;
                font-weight: var(--tp-font-subtitle-2-font-weight)!important;
                letter-spacing: var(--tp-font-subtitle-2-letter-spacing)!important;
                line-height: var(--tp-font-subtitle-2-line-height) !important;
                padding: 0 0 2px !important;
                text-transform: var(--tp-font-subtitle-2-text-transform);
            }
            
            #source.active,
            #source:hover {
                border-bottom: 2px solid var(--tp-menu-button-active-border-color);
            }
            
            ::slotted(*:not([slot="source"])) {
              --webkit-font-smoothing: antialiased;
              cursor: pointer;
              color: var(--tp-font-body-2-color);
              font-family: var(--tp-font-body-2-font-family);
              font-size: var(--tp-font-body-2-font-size);
              font-weight: var(--tp-font-body-2-font-weight);
              line-height: var(--tp-font-body-2-line-height);
              padding: var(--tp-menu-item-padding) !important;
              text-decoration: none;
              text-align: left;
              width: 100%;
            }
            
            ::slotted([selected]),
            ::slotted([active-item]) {
                background: var(--tp-menu-item-selected-color) !important;
                outline: none;
            }
            
            ::slotted([selected][active-item]:hover),
            ::slotted([selected][active-item]:active),
            ::slotted([selected][active-item]:focus)
            {
                background: var(--tp-menu-item-selected-active-item-color) !important;
            }
            
            #menu {
                background: #ffffff;
                padding: 6px 0;
            }
        </style>
      `
    );

    baseTemplate.content.querySelector('slot[name="source"]').outerHTML = '';

    return baseTemplate;
  }
}

export default TpMenu;
